/** Default time (ms) before a query is considered stale and eligible for background refetch. */
export const STALE_TIME_DEFAULT = 30_000;

/** Default time (ms) unused/inactive query data stays in cache before garbage collection. */
export const GC_TIME_DEFAULT = 5 * 60_000;

/** Default max retry attempts for failed queries. */
export const RETRY_COUNT_DEFAULT = 1;

/**
 * How often (ms) the health endpoint is polled while healthy. This is also the
 * worst-case delay before a passive user sees a version notice, so it is tuned
 * for that rather than for outage detection — `/healthz` is two cache reads.
 */
export const HEALTH_POLL_INTERVAL = 30_000;

/**
 * Staleness window (ms) for the health poll. Short so returning to a
 * backgrounded tab re-checks almost immediately, but non-zero so rapid tab
 * switching does not fire a request per switch.
 */
export const HEALTH_STALE_TIME_MS = 5_000;

/**
 * Faster health poll interval while the last check failed.
 * Lets confirmed-offline engage in seconds, not ~3×90s.
 */
export const HEALTH_POLL_INTERVAL_DEGRADED_MS = 5_000;

/** Consecutive failed health-poll cycles before confirmed offline (AD-1438). */
export const HEALTH_OFFLINE_FAILURE_THRESHOLD = 3;

/** Min gap between on-demand /healthz probes kicked by NETWORK_ERROR/TIMEOUT. */
export const HEALTH_PROBE_MIN_INTERVAL_MS = 2_000;

/** Whether to refetch queries when the browser window regains focus. */
export const REFETCH_ON_WINDOW_FOCUS = false;

/** Guard window (ms) preventing multiple concurrent 401 redirects. */
export const REDIRECT_GUARD_MS = 3_000;

/** Cache time (ms) for page-state queries (longer than default since page access rarely changes mid-session). */
export const PAGE_STATE_GC_TIME = 5 * 60_000;

/** Default poll interval (ms) for Amber Path status checks. */
export const AMBER_POLL_INTERVAL = 500;

/** Max poll attempts before timing out an Amber Path status check. */
export const AMBER_MAX_POLL_ATTEMPTS = 20;

/** Max backoff multiplier for exponential poll backoff. */
export const AMBER_MAX_BACKOFF_MULTIPLIER = 8;

/**
 * Poll cadence (ms) for filter-option lookups that the workspace worker resolves
 * against BigQuery. Faster than the 2s cadence the heavier Amber pages use: these
 * are cheap distinct-value queries and several of them gate the first paint of the
 * Explore filter bar. Mirrors the `EXAMPLES_POLL_INTERVAL_MS` precedent.
 */
export const OPTIONS_POLL_INTERVAL_MS = 750;

/**
 * Budget (ms) before an options poll is abandoned with a timeout error. Matches the
 * Amber page budget, and must stay under the backend `STATUS_TTL_SECONDS` so the
 * correlation status is still readable for the whole window. Lives here rather than
 * in a domain queries file to keep the shared poll helper free of circular imports.
 */
export const OPTIONS_POLL_TIMEOUT_MS = 5 * 60_000;

/**
 * Fresh window for the Org drawer's industry brand rosters and brand counts. Matches
 * the backend `OPTIONS_CACHE_TTL_SECONDS`, so refetching sooner re-reads the same entry.
 */
export const INDUSTRY_BRANDS_STALE_TIME_MS = 30 * 60_000;

/** Matches API `POST_FLIGHT_LIST_STALE_SECONDS` — list GET refetches upstream after this age. */
export const POST_FLIGHT_OPPORTUNITIES_STALE_TIME_MS = 12 * 60 * 60 * 1000;

/**
 * Matches `POST_FLIGHT_DOUBLE_DOWN_JOB_TTL_SECONDS` and
 * `DOUBLE_DOWN_IDEMPOTENCY_TTL_SECONDS` (3600). The detached double-down poll
 * has to cover that whole claim. A shorter attempt cap expires into
 * "Couldn't start generating scripts" while the worker is still running.
 */
export const POST_FLIGHT_DOUBLE_DOWN_POLL_BUDGET_MS = 60 * 60 * 1000;

/**
 * Default timeout (ms) for individual API requests before aborting and retrying.
 * Generous enough to ride out a cold-start GKE pod first hit (~5–10s on dev),
 * but tight enough that an unhealthy pod is detected and a retry hits a
 * healthy replica before the user notices. Heavy endpoints (large lists,
 * permission resolution) should opt into `API_REQUEST_TIMEOUT_LONG_MS` via
 * the `timeoutMs` option on `authFetch`.
 */
export const API_REQUEST_TIMEOUT_MS = 15_000;

/**
 * Extended timeout (ms) for legitimately-heavy endpoints — e.g. tenant-wide
 * list/facets queries, page-state with permission resolution. Covers cold
 * starts plus a real DB roundtrip without mid-request abort.
 */
export const API_REQUEST_TIMEOUT_LONG_MS = 30_000;

/** Large binary uploads (Pre Flight stage-memory in local dev). */
export const API_REQUEST_TIMEOUT_UPLOAD_MS = 10 * 60 * 1000;

/** Delay (ms) before the first retry attempt on a failed query. */
export const RETRY_DELAY_MS = 500;

// A report can run for up to an hour. Poll on a relaxing backoff anchored to
// a stable `pollStartedAt` so frequent early checks settle into cheap long
// intervals — and stop entirely on any terminal status.

/** Fast poll interval (ms) for the first window after a report is enqueued. */
export const PREFLIGHT_POLL_FAST_MS = 750;

/** Medium poll interval (ms) once the fast window elapses. */
export const PREFLIGHT_POLL_MED_MS = 5_000;

/** Slow poll interval (ms) for long-running reports. */
export const PREFLIGHT_POLL_SLOW_MS = 30_000;

/** Duration (ms) of the fast-poll window measured from `pollStartedAt`. */
export const PREFLIGHT_POLL_FAST_WINDOW_MS = 60_000;

/** Duration (ms) of the medium-poll window measured from `pollStartedAt`. */
export const PREFLIGHT_POLL_MED_WINDOW_MS = 5 * 60_000;

/**
 * Creative thumbnails are signed for 5 minutes server-side, so cached previews go stale
 * comfortably before the URLs they hold stop working.
 */
export const PREFLIGHT_PREVIEW_STALE_MS = 3 * 60_000;
